import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { github, templates, type Board, type GitHubRepoGroup, type TeamTemplate } from '../lib/api';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { BOARD_COLOR_KEYS, getBoardColorHex } from '../lib/utils';

interface Props {
  board: Board;
  currentRepo: { owner: string; repo: string; tokenId?: string } | null;
  onSave: (data: { name: string; description?: string; color?: string; githubRepo?: string; githubTokenId?: string; templateId?: string; claudeEnv: Record<string, string> }) => void;
  onDelete: () => void;
  onClose: () => void;
}

export function EditBoardModal({ board, currentRepo, onSave, onDelete, onClose }: Props) {
  const [name, setName] = useState(board.name);
  const [description, setDescription] = useState(board.description || '');
  // Option values in the <select> are `${tokenId}:${owner}/${repo}`, so the
  // initial value must match that shape or the dropdown falls back to "None".
  const [selectedRepo, setSelectedRepo] = useState(
    currentRepo
      ? currentRepo.tokenId
        ? `${currentRepo.tokenId}:${currentRepo.owner}/${currentRepo.repo}`
        : `${currentRepo.owner}/${currentRepo.repo}`
      : ''
  );
  const [selectedTemplate, setSelectedTemplate] = useState(board.settings?.defaultTemplate ?? '');
  const initialColor = (board.settings?.color as string | undefined) ?? BOARD_COLOR_KEYS[0];
  const [color, setColor] = useState<string>(initialColor);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [envVars, setEnvVars] = useState<Array<{ key: string; value: string }>>(() => {
    const env = board.settings?.runner?.claudeEnv;
    if (!env || Object.keys(env).length === 0) return [];
    return Object.entries(env).map(([key, value]) => ({ key, value }));
  });

  const { data: ghStatusData } = useQuery({
    queryKey: ['github-status'],
    queryFn: github.status,
  });

  const { data: templatesData } = useQuery({
    queryKey: ['templates'],
    queryFn: templates.list,
  });
  const templateList: TeamTemplate[] = templatesData?.data || [];

  const ghConnected = ghStatusData?.data?.connected ?? false;

  const { data: reposData, isLoading: reposLoading } = useQuery({
    queryKey: ['github-repos'],
    queryFn: github.repos,
    enabled: ghConnected,
  });

  const repoGroups: GitHubRepoGroup[] = reposData?.data || [];

  // Legacy boards store {owner, repo} without a tokenId. Once repo groups
  // load, try to match the saved repo to an available token-scoped option so
  // the dropdown shows the real selection instead of "None".
  useEffect(() => {
    if (!currentRepo || currentRepo.tokenId) return;
    if (repoGroups.length === 0) return;
    const fullName = `${currentRepo.owner}/${currentRepo.repo}`;
    const hasExactMatch = repoGroups.some((g) => g.repos?.some((r) => r.fullName === fullName));
    if (!hasExactMatch) return;
    const firstMatch = repoGroups.find((g) => g.repos?.some((r) => r.fullName === fullName));
    if (firstMatch) setSelectedRepo(`${firstMatch.tokenId}:${fullName}`);
  }, [repoGroups, currentRepo]);

  useEscapeKey(onClose);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const templateChanged = selectedTemplate !== (board.settings?.defaultTemplate ?? '');

    // Build claudeEnv from UI state, filtering out empty keys
    const claudeEnv: Record<string, string> = {};
    envVars.forEach(({ key, value }) => {
      if (key.trim()) claudeEnv[key.trim()] = value;
    });

    let githubRepo: string | undefined;
    let githubTokenId: string | undefined;
    if (selectedRepo) {
      const colonIdx = selectedRepo.indexOf(':');
      if (colonIdx !== -1) {
        githubTokenId = selectedRepo.substring(0, colonIdx);
        githubRepo = selectedRepo.substring(colonIdx + 1);
      } else {
        githubRepo = selectedRepo;
      }
    }

    // Only send color when the user explicitly picked something OR the board
    // already had a stored color. Avoids silently stamping 'red' onto legacy
    // uncolored boards on every edit.
    const colorChanged = color !== initialColor;
    const persistColor = colorChanged || board.settings?.color !== undefined;

    onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      color: persistColor ? color : undefined,
      githubRepo,
      githubTokenId,
      templateId: templateChanged ? selectedTemplate : undefined,
      claudeEnv, // always send — empty object means "clear all env vars"
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-md rounded-xl border border-gray-800 bg-surface-850 p-6 shadow-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold">Edit Board</h2>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Board Name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full resize-none rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
              placeholder="What's this board for?"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Board Color
            </label>
            <div className="flex flex-wrap gap-2">
              {BOARD_COLOR_KEYS.map((key) => {
                const selected = color === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setColor(key)}
                    className={`flex h-7 w-7 items-center justify-center rounded-full border-2 transition-colors ${
                      selected ? 'border-white' : 'border-transparent hover:border-gray-500'
                    }`}
                    style={{ background: getBoardColorHex(key) }}
                    title={key}
                    aria-label={`${key} color`}
                  />
                );
              })}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Team Template
            </label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
            >
              {templateList.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} (v{t.version})
                </option>
              ))}
            </select>
            {selectedTemplate !== (board.settings?.defaultTemplate ?? '') && (
              <p className="mt-1 text-[10px] text-orange-400">
                Changing the template will migrate columns to match the new workflow. Cards in removed columns will be displaced.
              </p>
            )}
          </div>

          {ghConnected && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                GitHub Repository
              </label>
              <select
                value={selectedRepo}
                onChange={(e) => setSelectedRepo(e.target.value)}
                className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
              >
                <option value="">
                  {reposLoading ? 'Loading repos...' : 'None'}
                </option>
                {repoGroups.map((group) => (
                  <optgroup key={group.tokenId} label={`${group.label} (@${group.username})`}>
                    {group.error ? (
                      <option disabled>Error: {group.error}</option>
                    ) : (
                      group.repos.map((r) => (
                        <option key={`${group.tokenId}:${r.fullName}`} value={`${group.tokenId}:${r.fullName}`}>
                          {r.fullName}{r.private ? ' (private)' : ''}
                        </option>
                      ))
                    )}
                  </optgroup>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Claude Environment
            </label>
            <p className="mb-2 text-[10px] text-gray-500">
              Environment variables passed to Claude CLI (e.g. CLAUDE_CONFIG_DIR)
            </p>
            <div className="space-y-2">
              {envVars.map((envVar, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    value={envVar.key}
                    onChange={(e) => {
                      const updated = [...envVars];
                      updated[i] = { ...updated[i], key: e.target.value };
                      setEnvVars(updated);
                    }}
                    placeholder="KEY"
                    className="w-1/3 rounded-lg border border-gray-700 bg-surface-900 px-2 py-1.5 font-mono text-xs text-gray-100 outline-none focus:border-primary-500"
                  />
                  <input
                    value={envVar.value}
                    onChange={(e) => {
                      const updated = [...envVars];
                      updated[i] = { ...updated[i], value: e.target.value };
                      setEnvVars(updated);
                    }}
                    placeholder="value"
                    className="flex-1 rounded-lg border border-gray-700 bg-surface-900 px-2 py-1.5 font-mono text-xs text-gray-100 outline-none focus:border-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => setEnvVars(envVars.filter((_, j) => j !== i))}
                    className="rounded-lg px-2 py-1.5 text-xs text-gray-500 hover:bg-red-500/10 hover:text-red-400"
                  >
                    &times;
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setEnvVars([...envVars, { key: '', value: '' }])}
                className="rounded-lg border border-dashed border-gray-700 px-3 py-1.5 text-xs text-gray-500 hover:border-gray-500 hover:text-gray-300"
              >
                + Add Variable
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <div>
            {!confirmDelete ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="rounded-lg px-3 py-2 text-xs font-medium text-red-400 hover:bg-red-500/10 hover:text-red-300"
              >
                Delete Board
              </button>
            ) : (
              <button
                type="button"
                onClick={onDelete}
                className="rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white hover:bg-red-500"
              >
                Confirm Delete
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim()}
              className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-40"
            >
              Save Changes
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
